import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Package, BookOpen, AlertTriangle } from "lucide-react";

export default function MagazzinoPubblico() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const taglia = searchParams.get("taglia");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/items/${id}`).then(r => setData(r.data))
      .catch(() => setError("Articolo non trovato"));
  }, [id]);

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <Card><CardContent className="p-8 text-center">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <h2 className="font-display text-2xl font-bold">{error}</h2>
      </CardContent></Card>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500">Caricamento...</div>;

  const { company, item } = data;
  const totalStock = Object.values(item.stock || {}).reduce((s, v) => s + v, 0);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-2 bg-warning-stripe" />
        <div className="max-w-3xl mx-auto px-6 py-8 relative">
          <div className="flex items-start gap-4 flex-wrap">
            <img src={company.logo_url} alt={company.name} className="h-14 w-auto bg-white p-1 rounded" />
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.3em] text-slate-400">Scheda articolo DPI</div>
              <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tighter">{item.name}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-300">
                <Badge className="bg-orange-500 text-white border-0">{item.category}</Badge>
                <span className="font-mono text-xs">{item.code}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {item.normative && (
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-orange-500" /> Riferimento normativo
              </h2>
              <p className="text-sm text-slate-600">{item.normative}</p>
            </CardContent>
          </Card>
        )}

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-orange-500" /> Disponibilità a magazzino
            </h2>
            <div className="text-sm text-slate-500 mb-3">Totale: <strong className="text-slate-800">{totalStock}</strong> pezzi</div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {(item.sizes || []).map(s => {
                const q = item.stock?.[s] || 0;
                const low = q <= (item.min_stock || 0);
                const highlighted = taglia && s === taglia;
                return (
                  <div key={s} className={`p-3 rounded border text-center ${highlighted ? "border-orange-500 bg-orange-50 ring-2 ring-orange-300" : low ? "border-red-200 bg-red-50" : "border-slate-200 bg-white"}`}>
                    <div className="text-[10px] uppercase text-slate-500">{s}</div>
                    <div className="font-bold text-lg">{q}</div>
                    {low && <div className="text-[9px] text-red-600 mt-0.5">scorta bassa</div>}
                    {highlighted && <div className="text-[9px] text-orange-600 font-semibold mt-0.5">taglia scansionata</div>}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400 py-4">
          {company.name} · Scheda generata automaticamente
        </p>
      </main>
    </div>
  );
}
